import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { likesAPI, profileAPI } from '../services/api';
import './FloatingActions.css';

const VIP_COUNT_CACHE_KEY = 'gsc_vip_members_count';

const formatVipCount = (value) => {
  const digits = String(value ?? '').replace(/[^\d]/g, '');
  const num = parseInt(digits, 10);
  if (!Number.isFinite(num) || num <= 0) return '';
  return num.toLocaleString('en-US');
};

const readCachedVipCount = () => {
  try {
    return formatVipCount(localStorage.getItem(VIP_COUNT_CACHE_KEY)) || '';
  } catch (err) {
    return '';
  }
};

const cacheVipCount = (formatted) => {
  try {
    const digits = String(formatted).replace(/[^\d]/g, '');
    if (digits) localStorage.setItem(VIP_COUNT_CACHE_KEY, digits);
  } catch (err) {
    // ignore quota / private mode
  }
  return formatted;
};

const SUPPORT_EMAIL = 'support@johnstrategy.com';

const FloatingActions = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [likes, setLikes] = useState('0');
  const [hasLikedToday, setHasLikedToday] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [joinCommunityLink, setJoinCommunityLink] = useState('/register');
  const [joinCommunityText, setJoinCommunityText] = useState('Join Community');
  const [vipMembersCount, setVipMembersCount] = useState(readCachedVipCount);
  const [emailOpen, setEmailOpen] = useState(false);
  const emailWrapRef = useRef(null);

  useEffect(() => {
    if (!emailOpen) return undefined;
    const onPointerDown = (e) => {
      if (emailWrapRef.current && !emailWrapRef.current.contains(e.target)) {
        setEmailOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setEmailOpen(false); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [emailOpen]);

  // Simple toast function
  const showToast = (message, type = 'info') => {
    setToastMessage({ message, type });
    setTimeout(() => {
      setToastMessage(null);
    }, 3000);
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Format like count
  const formatLikes = (count) => {
    const num = parseInt(count, 10) || 0;
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Get like count and check if already liked
  useEffect(() => {
    fetchLikes();
    checkLikedToday();
    fetchProfileConfig();
  }, []);

  const fetchProfileConfig = async () => {
    try {
      const response = await profileAPI.get();
      if (response.data.success && response.data.data.profile) {
        const profile = response.data.data.profile;
        if (profile.joinCommunityLink) {
          setJoinCommunityLink(profile.joinCommunityLink);
        }
        if (profile.joinCommunityText) {
          setJoinCommunityText(profile.joinCommunityText);
        }
        const formatted = formatVipCount(profile.vipMembersCount);
        if (formatted) {
          setVipMembersCount(cacheVipCount(formatted));
        }
      }
    } catch (error) {
      console.error('Fetch profile config error:', error);
    }
  };

  const fetchLikes = async () => {
    try {
      // Get like count from profile API (backend configured value)
      const response = await profileAPI.get();
      if (response.data.success && response.data.data.profile) {
        // Get totalLikes from profile, if not available get from likes API
        const profileLikes = response.data.data.profile.totalLikes;
        if (profileLikes !== undefined && profileLikes !== null) {
          setLikes(formatLikes(profileLikes));
        } else {
          // If not in profile, get actual like count from likes API
          const likesResponse = await likesAPI.get();
          if (likesResponse.data.success) {
            setLikes(likesResponse.data.data.formattedLikes || '0');
          }
        }
      }
    } catch (error) {
      console.error('Fetch likes error:', error);
      // If profile API fails, try to get from likes API
      try {
        const likesResponse = await likesAPI.get();
        if (likesResponse.data.success) {
          setLikes(likesResponse.data.data.formattedLikes || '0');
        }
      } catch (likesError) {
        console.error('Fetch likes from API error:', likesError);
      }
    }
  };

  const checkLikedToday = async () => {
    try {
      const response = await likesAPI.check();
      if (response.data.success) {
        setHasLikedToday(response.data.data.hasLikedToday || false);
      }
    } catch (error) {
      console.error('Check likes error:', error);
    }
  };

  const handleLike = async () => {
    if (hasLikedToday) {
      showToast("You've already liked today. Please come back tomorrow!", 'warning');
      return;
    }

    if (isLiking) {
      return;
    }

    setIsLiking(true);
    try {
      const response = await likesAPI.like();
      if (response.data.success) {
        setHasLikedToday(true);
        showToast('Like added successfully!', 'success');
        // After successful like, re-fetch like count from profile (backend will update totalLikes)
        await fetchLikes();
      } else {
        // If backend returns "already liked today" message, also update state
        if (response.data.message && response.data.message.includes('already liked')) {
          setHasLikedToday(true);
          showToast("You've already liked today. Please come back tomorrow!", 'warning');
        } else {
          showToast(response.data.message || 'Failed to like.', 'error');
        }
      }
    } catch (error) {
      // Handle 400 error (already liked case)
      if (error.response?.status === 400 && error.response?.data?.message) {
        const errorMessage = error.response.data.message;
        if (errorMessage.includes('already liked') || errorMessage.includes('come back tomorrow')) {
          setHasLikedToday(true);
          showToast("You've already liked today. Please come back tomorrow!", 'warning');
        } else {
          showToast(errorMessage, 'warning');
        }
      } else if (error.response?.data?.message) {
        showToast(error.response.data.message, 'error');
      } else {
        showToast('Failed to like. Please try again later.', 'error');
      }
      console.error('Like error:', error);
    } finally {
      setIsLiking(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCopyEmail = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(SUPPORT_EMAIL);
      } else {
        const ta = document.createElement('textarea');
        ta.value = SUPPORT_EMAIL;
        ta.style.position = 'fixed';
        ta.style.top = '-1000px';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      showToast(`Copied: ${SUPPORT_EMAIL}`, 'success');
    } catch (err) {
      showToast(`Copy failed. Email: ${SUPPORT_EMAIL}`, 'error');
    }
  };


  const handleJoinCommunity = () => {
    // 1) 优先使用后台配置的 Join Community 链接（来自 /api/profile -> joinCommunityLink）
    if (joinCommunityLink) {
      const link = String(joinCommunityLink).trim();
      if (link) {
        if (link.startsWith('http://') || link.startsWith('https://')) {
          window.open(link, '_blank', 'noopener,noreferrer');
        } else {
          // 内部相对路径，例如 /register
          window.location.href = link;
        }
        return;
      }
    }

    // 2) 没配置链接时退化到 SaleSmartly 客服弹窗
    if (window.ssmEvent && typeof window.ssmEvent.track === 'function') {
      window.ssmEvent.track('chatOpen');
      return;
    }
    if (window.ssq) {
      window.ssq.push(['chatOpen']);
      return;
    }

    // 3) 兜底
    alert('Please click the chat icon at the bottom right corner.');
  };

  const stats = {
    vipMembers: vipMembersCount,
  };

  return createPortal(
    <>
      {/* Toast Message */}
      {toastMessage && (
        <div className={`floating-actions__toast floating-actions__toast--${toastMessage.type}`}>
          {toastMessage.message}
        </div>
      )}
      
      <div className="floating-actions">
        {/* Quick Stats */}
      <div className="floating-actions__stats">
        {/* Support Email - opens a popup */}
        <div className="floating-actions__email-wrap" ref={emailWrapRef}>
          <button
            type="button"
            onClick={() => setEmailOpen((v) => !v)}
            className={`floating-actions__stat-item floating-actions__stat-item--email ${emailOpen ? 'floating-actions__stat-item--email-open' : ''}`}
            aria-expanded={emailOpen}
            aria-haspopup="dialog"
            title="Contact support"
          >
            <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
              <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Email</span>
          </button>
          {emailOpen && (
            <div className="floating-actions__email-popup" role="dialog" aria-label="Contact support">
              <div className="floating-actions__email-popup__eyebrow">Contact Support</div>
              <div className="floating-actions__email-popup__addr" title="Select to copy manually">
                {SUPPORT_EMAIL}
              </div>
              <div className="floating-actions__email-popup__hint">
                We usually reply within 24 hours.
              </div>
              <div className="floating-actions__email-popup__actions">
                <button
                  type="button"
                  className="floating-actions__email-popup__btn floating-actions__email-popup__btn--copy"
                  onClick={handleCopyEmail}
                >
                  <svg viewBox="0 0 24 24" fill="none" width="14" height="14">
                    <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M5 15H4C2.9 15 2 14.1 2 13V4C2 2.9 2.9 2 4 2H13C14.1 2 15 2.9 15 4V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  Copy email
                </button>
              </div>
            </div>
          )}
        </div>

        <Link to="/leaderboard" className="floating-actions__stat-item floating-actions__stat-item--lb">
          <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
            <path d="M16 8V16M12 11V16M8 14V16M6 20H18C19.1046 20 20 19.1046 20 18V6C20 4.89543 19.1046 4 18 4H6C4.89543 4 4 4.89543 4 6V18C4 19.1046 4.89543 20 6 20Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>LB</span>
        </Link>
        
        <Link to="/vip" className="floating-actions__stat-item floating-actions__stat-item--vip">
          <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>VIP</span>
          {stats.vipMembers ? (
            <span className="floating-actions__badge">{stats.vipMembers}</span>
          ) : null}
        </Link>
        
        <button 
          className={`floating-actions__stat-item floating-actions__stat-item--likes ${hasLikedToday ? 'floating-actions__stat-item--liked' : ''}`}
          onClick={handleLike}
          disabled={isLiking || hasLikedToday}
          title={hasLikedToday ? 'Already liked today' : 'Click to like'}
        >
          <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              fill={hasLikedToday ? "currentColor" : "none"}
            />
          </svg>
          <span>Likes</span>
          <span className="floating-actions__badge floating-actions__badge--likes">{likes}</span>
        </button>
      </div>

      {/* Join Community Button */}
      <button 
        className="floating-actions__join" 
        onClick={handleJoinCommunity}
        title={joinCommunityLink ? `Click to go to: ${joinCommunityLink}` : 'Join Community'}
      >
        <span className="floating-actions__join-icon">
          <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
            <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13M16 3.13C16.8604 3.3503 17.623 3.8507 18.1676 4.55231C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88M13 7C13 9.20914 11.2091 11 9 11C6.79086 11 5 9.20914 5 7C5 4.79086 6.79086 3 9 3C11.2091 3 13 4.79086 13 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
        <span className="floating-actions__join-text">{joinCommunityText}</span>
      </button>

      {/* Scroll to Top */}
      <button 
        className={`floating-actions__scroll-top ${showScrollTop ? 'floating-actions__scroll-top--visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Scroll to top"
      >
        <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
          <path d="M12 19V5M12 5L5 12M12 5L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
    </>,
    document.body
  );
};

export default FloatingActions;

